import { useEffect } from 'react';
import { useUser, studioThemeClass } from '../contexts/UserContext';

/**
 * Studio colour theme for a page root.
 *
 * Returns the class string to put on the page's outermost element, and — the
 * part that is easy to miss — mirrors the same theme class onto <body> for as
 * long as the page is mounted.
 *
 * The mirror is not belt-and-braces. Exit confirms, credit warnings and job
 * forms are rendered with `createPortal(…, document.body)`, so they sit
 * OUTSIDE the page's root div: a `.cls-light .foo` rule scoped to that root
 * can never match them, and they stay dark while the page around them goes
 * light. Scoping from body covers the portals and the in-tree markup alike.
 *
 * Pass `enabled: false` for embedded renders (landing-page previews, the
 * Electron pane) — those surfaces are dark by design and take no theme.
 *
 * @param {boolean} enabled
 * @returns {string} e.g. `"cls-page cls-light"`, or `""` when disabled
 */
/**
 * How many mounted hooks currently want each class on <body>.
 *
 * The mirror used to be a plain add-on-mount / remove-on-cleanup pair, which
 * is only correct while exactly one component uses the hook. With more than
 * one — the app shell plus a page, say — the FIRST to unmount stripped the
 * class off <body> even though the others still needed it, and nothing put
 * it back because their effects had already run and their deps had not
 * changed. Symptom: portalled dialogs (which live outside the page root and
 * can only be reached from `body.cls-light`) silently reverted to dark on
 * routes that redirect, because the unmounted page took the class with it.
 *
 * Module scope on purpose: the count is per-document, not per-component.
 */
const bodyClassOwners = new Map();

export function useStudioTheme(enabled = true) {
  const { studioTheme } = useUser();
  const themeClass = studioThemeClass(studioTheme);

  useEffect(() => {
    if (!enabled) return undefined;
    // Dark is the absence of a class, so there may be nothing to add.
    const classes = themeClass.split(' ').filter(Boolean);
    if (!classes.length) return undefined;
    classes.forEach((c) => {
      const next = (bodyClassOwners.get(c) || 0) + 1;
      bodyClassOwners.set(c, next);
      if (next === 1) document.body.classList.add(c);
    });
    return () => {
      classes.forEach((c) => {
        const next = (bodyClassOwners.get(c) || 0) - 1;
        if (next > 0) {
          bodyClassOwners.set(c, next);
        } else {
          bodyClassOwners.delete(c);
          document.body.classList.remove(c);
        }
      });
    };
  }, [enabled, themeClass]);

  return enabled ? `cls-page ${themeClass}` : '';
}

export default useStudioTheme;
